import { ConfigurationType } from '../types';
import { isAiInputAllowed } from './configurationChatConsts';
import { AiChatInput } from '../../../common/ai-chat/AiChatInput';
import { useState } from 'react';
import { Button, Typography } from '@sweep-io/sweep-design';
import Stack from '@mui/material/Stack';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { HOMEPAGE_EVENTS } from '../../../../services/events';
import useBookDemoClickHandler from '../../../../hooks/useBookDemoClickHandler';
import usePaywall from '../../../dashboard/paywall/usePaywall';

const symbol = '$item';
const prompts: { [key: string]: string } = {
  ai1: 'Break down in steps how this $item works',
  ai2: 'List all fields that are updated via this $item',
};

const excludeAI2FromTypes = [ConfigurationType.validationRules];

interface ConfigurationChatInputWrapperProps {
  onConfirm: (message: string) => void;
  isLoading: boolean;
  configurationKey: ConfigurationType;
  itemType?: string;
  disableInput: boolean;
  messagesCount: number;
}

export const ConfigurationChatInputWrapper = ({
  configurationKey,
  onConfirm,
  isLoading,
  itemType,
  disableInput,
  messagesCount,
}: ConfigurationChatInputWrapperProps) => {
  const { isPaywallActive } = usePaywall();
  const [value, setValue] = useState('');

  const isField =
    configurationKey === ConfigurationType.fields || configurationKey === ConfigurationType.rollups;
  const isCpq = configurationKey === ConfigurationType.cpqData;

  if (!isAiInputAllowed(configurationKey)) {
    return <></>;
  }

  const noMessagesSent = messagesCount === 0;
  const isNoPromptsType = isField || isCpq;

  const predefinedPrompts = isNoPromptsType
    ? {}
    : Object.keys(prompts)
        .filter(
          (key) =>
            key !== 'ai2' || (key === 'ai2' && !excludeAI2FromTypes.includes(configurationKey)),
        )
        .reduce(
          (obj, key) => {
            obj[key] = prompts[key].replace(symbol, itemType?.toLowerCase() ?? 'element');
            return obj;
          },
          {} as { [promptKey: string]: string },
        );

  const promptsToRender = isPaywallActive ? {} : predefinedPrompts;

  return (
    <AiChatInput
      value={value}
      setValue={setValue}
      textFieldVariant="primary"
      predefinedPrompts={
        noMessagesSent && !isNoPromptsType ? promptsToRender : isPaywallActive ? {} : undefined
      }
      onConfirm={onConfirm}
      isLoading={isLoading}
      isCollapsiblePrompts={true}
      disabled={disableInput || isPaywallActive}
      headerText={isPaywallActive ? <NoQuestionsLeftHeader /> : undefined}
    />
  );
};

const NoQuestionsLeftHeader = () => {
  const sendBiEvent = useSendBiEvent();
  const onBookDemoClick = useBookDemoClickHandler();

  return (
    <Stack
      direction="row"
      gap={0.5}
      alignItems="center"
      sx={{
        '& button': {
          fontSize: '16px',
        },
      }}
    >
      <Typography variant="h5">Not included in Free tier.</Typography>
      <Button
        variant="flat-blue"
        underlined={true}
        onClick={() => {
          sendBiEvent({ name: HOMEPAGE_EVENTS.upgradeFreeQuestions });
          onBookDemoClick();
        }}
      >
        Talk to us
      </Button>
    </Stack>
  );
};
